import Axios from 'axios'
import { format } from 'date-fns'
import { formatDuration } from './format'
import { isPromise, isString } from './is'
import { isWindows, isMacOS } from './ua'
import { enc } from 'crypto-js'
import i18n from '@/i18n'

function getTextEncoder(encoder) {
  if (!encoder) {
    return enc.Utf8
  }
  if (isString(encoder)) {
    return (enc[encoder] || enc.Utf8)
  }
  return encoder
}

export function encryptBase64(rawStr, textEncoder) {
  return enc.Base64.stringify(getTextEncoder(textEncoder).parse(rawStr))
}

export function decryptBase64(base64Str, textEncoder) {
  return enc.Base64.parse(base64Str).toString(getTextEncoder(textEncoder))
}

export function patchList(items, { patchFunc, limit, defPatchData, clone }) {
  const len = items.length
  let _copyItems = clone === true ? null : items

  limit = limit || Infinity

  let changeCount = 0

  for (let i = 0; i < len; i++) {
    const curr = items[i]
    const patchRes = patchFunc(curr)
    if (patchRes) {
      if (_copyItems === null) {
        _copyItems = items.slice(0)
      }

      // 不要去掉这句代码：此时patchRes不是true就是一个object
      if (patchRes === true) {
        // 当patchRes属于布尔值true时
        _copyItems.splice(i, 1, { ...curr, ...defPatchData })
      } else {
        // 当patchRes是个对象时
        _copyItems.splice(i, 1, { ...curr, ...patchRes })
      }
      changeCount++
      if (changeCount >= limit) {
        break
      }
    }
  }
  if (changeCount === 0) {
    return items
  }
  return _copyItems
}

/**
 * 向元素唯一的数组中倒序添加元素
 */
export function descInsertList(item, list) {
  const len = list.length
  if (len > 0) {
    if (item > list[0]) {
      list.unshift(item)
    } else {
      for (let k = len - 1; k > -1; k--) {
        if (list[k] === item) {
          return false
        }
        if (item < list[k]) {
          list.splice(k + 1, 0, item)
          break
        }
      }
    }
  } else {
    list.push(item)
  }
  return true
}

export function descObjInsertList(item, list, compareField) {
  const len = list.length

  const tv = item[compareField]
  if (len > 0) {
    if (tv > list[0][compareField]) {
      list.unshift(item)
    } else {
      for (let k = len - 1; k > -1; k--) {
        const curr = list[k][compareField]
        if (curr === tv) {
          return false
        }
        if (tv < curr) {
          list.splice(k + 1, 0, item)
          break
        }
      }
    }
  } else {
    list.push(item)
  }
  return true
}

// 作为默认函数使用
export function doNothing(target) {
  return target
}

// 校验密码强度
export function checkPassWordComplexity(value) {
  var score = 0
  var intensity = '很短'
  var color = '#f5222d'
  if (/\d/.test(value)) {
    // 如果用户输入的密码 包含了数字
    score += 20
  }
  if (/[a-z]/.test(value)) {
    // 如果用户输入的密码 包含了小写的a到z
    score += 20
  }
  if (/[A-Z]/.test(value)) {
    // 如果用户输入的密码 包含了大写的A到Z
    score += 20
  }
  if (/\W/.test(value)) {
    // 如果是非数字 字母 下划线
    score += 20
  }

  switch (score) {
    case 20:
      color = '#f5222d'
      intensity = '很短'
      break
    case 40:
      color = '#f5222d'
      if (value.length >= 6) {
        intensity = '弱'
      }
      break
    case 60:
      color = '#faad14'
      if (value.length >= 6) {
        intensity = '中'
      }
      break
    case 80:
      color = '#52c41a'
      if (value.length >= 6) {
        intensity = '强'
      }
      break
  }
  return { score, intensity, color }
}

// 创建枚举对象
export function createEnums(obj) {
  const enumObj = { ...obj }
  for (const key in enumObj) {
    enumObj[enumObj[key]] = key
  }
  return enumObj
}

// 防止重复点击
export function doubleCheck(fn, time = 1000){
  let check = false
  return function(){
    if (check) return
    check = true
    fn.apply(this, arguments)
    setTimeout(_ => {
      check = false
    }, time)
  }
}

// 节流
export function throttle(fn, time) {
  let canRun = true
  return (...args) => {
    if (!canRun) return
    canRun = false
    setTimeout(() => {
      fn(...args)
      canRun = true
    }, time)
  }
}

// 防抖
export function debounce(fn, time) {
  let timeout = null
  return (...args) => {
    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = window.setTimeout(() => {
      fn(...args)
    }, time)
  }
}

// 防抖 + 防请求延迟
export function fetchDebounce(fn, time) {
  let timeout = null
  let cancel = null

  return (...args) => {
    if (timeout) {
      clearTimeout(timeout)
    }
    if (cancel) {
      cancel()
    }
    return new Promise((resolve, reject) => {
      timeout = window.setTimeout(() => {
        const source = Axios.CancelToken.source()
        cancel = source.cancel
        fn(source, ...args)
          .then(resolve)
          .catch(reject)
          .finally(() => {
            cancel = null
          })
      }, time)
    })
  }
}

/**
 *
 * @param {import('moment')} momentIst
 * @returns
 */
export function pureMoment(momentIst) {
  return momentIst.hours(0).minutes(0).seconds(0).milliseconds(0)
}

// 格式化时间段
export function calcTimeRange({ beginTime, endTime }) {
  const beginDate = new Date(beginTime * 1000)
  const endDate = new Date(endTime * 1000)

  let _endTime = null

  if (format(beginDate, 'yyyyMMdd') === format(endDate, 'yyyyMMdd')) {
    _endTime = format(endDate, 'HH:mm')
  } else {
    _endTime = format(endDate, i18n.tc('common.DateFormat'))
  }

  return {
    get beginTime() { return format(beginDate, i18n.tc('common.DateFormat')) },
    endTime: _endTime,
    get formatDuration () { return formatDuration(endTime - beginTime) }
  }
}

export function linkOps(cb) {
  const anchor = document.createElement('a')
  anchor.target = '_blank'
  const ret = cb(anchor)
  anchor.click()
  if (isPromise(ret)) {
    ret.finally(() => {
      anchor.remove()
    })
  } else {
    anchor.remove()
  }
}

export function buildIcsContent({ start, end, subject = '', description = '', position = '' }) {
  const formatTemp = 'yyyyMMdd\'T\'HH:mm:ss'
  const dtStart = format(start, formatTemp)
  const dtEnd = format(end, formatTemp)
  return `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nURL:${location.origin}\nMETHOD:PUBLISH\nDTSTART:${dtStart}Z\nDTEND:${dtEnd}Z\nSUMMARY:${subject}\nDESCRIPTION:${description}\nLOCATION:${position}\nEND:VEVENT\nEND:VCALENDAR`
}

// 导入日历
export function exportCalendar(args) {
  let hasError = true
  try {
    if (isWindows) {
      linkOps((anchor) => {
        const formatTemp = 'yyyy-MM-dd\'T\'HH:mm:ss'
        const { start, end, subject = '' } = args
        const dtStart = format(start, formatTemp)
        const dtEnd = format(end, formatTemp)
        anchor.href = `outlookcal:addevent?"start=${dtStart}&end=${dtEnd}&subject=${subject}&location=&applaunch=TaskbarCalendarLaunch"`
      })
    } else if (isMacOS) {
      linkOps((anchor) => {
        anchor.href = `webcal://${location.hostname}/hiapigw/webCal?data=${encryptBase64(buildIcsContent(args))}`
      })
    }
    hasError = false
  } finally {
    if (hasError || !(isWindows || isMacOS)) {
      linkOps((anchor) => {
        anchor.download = i18n.tc('home.ScheduleOfAppointmentCollaboration') + '.ics'
        anchor.href = `data:text/calendar;base64,${encryptBase64(buildIcsContent(args))}`
      })
    }
  }
}

// 获取浏览器类型
export function checkBrowser() {
  const userAgentString =
  navigator.userAgent

  if (userAgentString.indexOf('OP') > -1) {
    return 'Opera'
  }

  if (userAgentString.indexOf('Chrome') > -1) {
    return 'Chrome'
  }

  if (userAgentString.indexOf('MSIE') > -1 || userAgentString.indexOf('rv:') > -1) {
    return 'IE'
  }

  if (userAgentString.indexOf('Firefox') > -1) {
    return 'Firefox'
  }

  if (userAgentString.indexOf('Edg') > -1) {
    return 'Edg'
  }

  if (userAgentString.indexOf('Safari') > -1) {
    return 'Safari'
  }
}

// 获取系统类型
export function getSystemType() {
  return new Promise((resolve) => {
    if (window.navigator.userAgent.indexOf('Mac') !== -1) {
      resolve('Mac/iOS')
    }
    if (window.navigator.userAgent.indexOf('X11') !== -1) {
      resolve('UNIX')
    }
    if (window.navigator.userAgent.indexOf('Linux') !== -1) {
      resolve('Linux')
    }
    try {
      navigator.userAgentData.getHighEntropyValues(['platformVersion'])
        .then(ua => {
          if (navigator.userAgentData.platform === 'Windows') {
            const majorPlatformVersion = parseInt(ua.platformVersion.split('.')[0])
            if (majorPlatformVersion >= 13) {
              resolve('Windows 11 or later')
            } else if (majorPlatformVersion > 0) {
              resolve('Windows 10')
            } else {
              resolve('Before Windows 10')
            }
          }
        })
    } catch (err) {
      resolve('Unknown')
    }
  })
}
